import { useState } from 'react'
import PropTypes from 'prop-types'
import axios from 'axios'
import { useEditor } from '../../../../../../App.jsx'
import { IconUpload } from '../../../../../Customiz3r/components/ToolSelectorTop/Icons.jsx'
import Icon from '@mdi/react'
import { mdiContentSaveOutline } from '@mdi/js'
import { showConfirm, showErrorAlert } from '../../../../../../components/HelperAlerts/HelpersAlerts.jsx'

// Upscale una imagen en el cliente usando Canvas (bicubic del browser)
// Devuelve un File PNG con la imagen escalada
async function upscaleOnClient(file, scale) {
    if (scale <= 1) return file
    return new Promise((resolve) => {
        const img = new Image()
        img.onload = () => {
            const canvas = document.createElement('canvas')
            canvas.width  = img.naturalWidth  * scale
            canvas.height = img.naturalHeight * scale
            const ctx = canvas.getContext('2d')
            ctx.imageSmoothingEnabled = true
            ctx.imageSmoothingQuality = 'high'
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
            canvas.toBlob(blob => {
                resolve(new File([blob], file.name, { type: 'image/png' }))
            }, 'image/png')
        }
        img.src = URL.createObjectURL(file)
    })
}

const MODES = [
    { value: 'raw',    label: 'Sin modificaciones', hint: 'Output exacto del motor' },
    { value: 'curves', label: 'Sanitizar Bezier', hint: 'Elimina curvas degeneradas' },
    { value: 'points', label: 'Puntos de ancla', hint: 'Recomendado para 3D' },
]
const ENGINES = [
    { value: 'potrace',  label: 'Potrace',  hint: 'Rápido' },
    { value: 'inkscape', label: 'Inkscape', hint: 'Trace Bitmap real' },
]
const VIEWS = ['original', 'vectorizado', 'superpuesto']

const getInitialName = (file, url) => {
    if (file) return file.name.replace(/\.[^.]+$/, '')
    if (!url) return ''

    try {
        return new URL(url).pathname.split('/').pop().replace(/\.[^.]+$/, '')
    } catch {
        return ''
    }
}

// `onCreated`/`onClose` opcionales: si se pasan (modo standalone, p. ej. el
// explorador del customiz3r) tras guardar se llaman en vez de navegar por el
// stateEditor del perfil. Sin ellos, mantiene el comportamiento original.
const MediosInputImgEditor = ({ onCreated, onClose, initialFile, initialUrl = '' }) => {
    const { stateEditor, setStateEditor } = useEditor()
    const standalone = typeof onCreated === 'function'

    const [source, setSource]       = useState(initialUrl ? 'url' : 'file')  // 'file' | 'url'
    const [file, setFile]           = useState(initialFile || null)
    const [imageUrl, setImageUrl]   = useState(initialUrl)
    const [preview, setPreview]     = useState(() => initialFile ? URL.createObjectURL(initialFile) : initialUrl || null)
    const [nombre, setNombre]       = useState(() => getInitialName(initialFile, initialUrl))
    const [sharing, setSharing]     = useState('privado')
    const [nColors, setNColors]     = useState(2)
    const [mode, setMode]           = useState('raw')
    const [engine, setEngine]       = useState('inkscape')
    const [epsilon, setEpsilon]     = useState(0.002)
    const [svgResult, setSvgResult] = useState(null)
    const [layers, setLayers]       = useState([])
    const [loading, setLoading]     = useState(false)
    const [errors, setErrors]       = useState({})
    const [view, setView]           = useState('original')
    const [sanitize, setSanitize]       = useState(false)
    const [upscale, setUpscale]         = useState(1)
    const [removeBg, setRemoveBg]       = useState(true)
    const [bgThreshold, setBgThreshold] = useState(30)
    const [showAdvanced, setShowAdvanced] = useState(false)
    const [blacklevel, setBlacklevel]   = useState(0.45)
    const [alphamax, setAlphamax]       = useState(1.0)
    const [opttolerance, setOpttolerance] = useState(0.2)
    const [turdsize, setTurdsize]       = useState(2)
    const [smooth, setSmooth]           = useState(false)  // Suave
    const [stack, setStack]             = useState(false)  // Apilar

    function applyInkscapePreset() {
        setNColors(2); setMode('raw'); setEngine('inkscape')
        setRemoveBg(true); setSanitize(false)
        setAlphamax(1.0); setOpttolerance(0.2); setTurdsize(2)
        setSmooth(false); setStack(false)
        setUpscale(1); setShowAdvanced(true)
    }

    function handleFileChange(e) {
        const f = e.target.files[0]
        if (!f) return
        setSource('file')
        setFile(f)
        setImageUrl('')
        setPreview(URL.createObjectURL(f))
        setSvgResult(null)
        setLayers([])
        setView('original')
        if (!nombre) setNombre(f.name.replace(/\.[^.]+$/, ''))
        setErrors({})
    }

    function handleUrlChange(value) {
        setSource('url')
        setImageUrl(value)
        setFile(null)
        setSvgResult(null)
        setLayers([])
        setView('original')
        const trimmed = value.trim()
        setPreview(trimmed || null)
        if (trimmed && !nombre) {
            const base = trimmed.split('/').pop().split('?')[0].replace(/\.[^.]+$/, '')
            if (base) setNombre(base)
        }
        setErrors({})
    }

    async function handleVectorize() {
        if (source === 'file' && !file) return
        if (source === 'url' && !imageUrl.trim()) return
        setLoading(true)
        setErrors({})

        try {
            const formData = new FormData()
            if (source === 'url') {
                // El backend descarga la URL (evita CORS y permite cualquier formato)
                formData.append('url', imageUrl.trim())
            } else {
                const fileToSend = await upscaleOnClient(file, upscale)
                formData.append('file', fileToSend)
            }
            formData.append('n_colors', nColors)
            formData.append('mode', mode)
            formData.append('engine', engine)
            formData.append('sanitize',      sanitize  ? 'true' : 'false')
            formData.append('remove_bg',     removeBg  ? 'true' : 'false')
            formData.append('bg_threshold',  bgThreshold)
            formData.append('blacklevel',    blacklevel)
            formData.append('alphamax',      alphamax)
            formData.append('opttolerance',  opttolerance)
            formData.append('turdsize',      turdsize)
            formData.append('smooth',        smooth ? 'true' : 'false')
            formData.append('stack',         stack  ? 'true' : 'false')
            if (mode === 'points') formData.append('epsilon', epsilon)

            const { data } = await axios.post('vectorize/', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            })
            setSvgResult(data.svg)
            setLayers(data.layers)
            setView('vectorizado')
        } catch (err) {
            setErrors({ vectorize: err.response?.data?.error || 'Error al vectorizar. Probá con menos colores.' })
        } finally {
            setLoading(false)
        }
    }

    function validate() {
        const e = {}
        if (!nombre.trim()) e.nombre = 'El nombre es obligatorio.'
        if (!sharing) e.sharing = 'Seleccioná una opción.'
        if (!svgResult) e.svg = 'Primero vectorizá la imagen.'
        setErrors(e)
        return Object.keys(e).length === 0
    }

    async function handleSubmit(ev) {
        ev.preventDefault()
        if (!validate()) return

        const svgBase64 = btoa(unescape(encodeURIComponent(svgResult)))

        try {
            const { data } = await axios.post('media/', {
                medio: { svg: svgBase64, nombre, sharing_permissions: sharing },
            })

            if (standalone) {
                onCreated(data)
                onClose?.()
                return
            }

            showConfirm({
                title: 'Medio creado',
                text: '¿Qué querés hacer ahora?',
                icon: 'success',
                showCancelButton: true,
                showDenyButton: true,
                confirmButtonText: 'Volver a la lista',
                denyButtonText: 'Añadir otro',
                cancelButtonText: 'Quedarse aquí',
            }).then((result) => {
                if (result.isConfirmed) {
                    setStateEditor({ ...stateEditor, editor: false, editorID: null })
                } else if (result.isDenied) {
                    setFile(null); setPreview(null); setSvgResult(null)
                    setLayers([]); setNombre(''); setView('original')
                } else {
                    setStateEditor({ ...stateEditor, editor: true, editorID: data.id })
                }
            })
        } catch (err) {
            showErrorAlert({
                text: err.response?.data?.error || 'No se pudo guardar el medio.',
                confirmButtonText: 'Entendido',
            })
        }
    }

    return (
        <div className="h-full w-full">
            <form onSubmit={handleSubmit} className="flex flex-col md:flex-row landscape:flex-row h-full gap-3 w-full">

                {/* Panel izquierdo: visor */}
                <div className="flex flex-col w-full glassCard md:w-2/5 landscape:w-2/5 landscape:h-full h-1/2 md:h-full flex-1 overflow-hidden">

                    {/* Tabs de vista */}
                    {(preview || svgResult) && (
                        <div className="flex border-b border-base-300 shrink-0">
                            {VIEWS.map(v => (
                                <button
                                    key={v}
                                    type="button"
                                    onClick={() => setView(v)}
                                    disabled={v !== 'original' && !svgResult}
                                    className={`flex-1 py-1.5 text-xs capitalize transition-colors
                                        ${view === v ? 'border-b-2 border-primary font-semibold text-primary' : 'text-base-content/50'}
                                        ${v !== 'original' && !svgResult ? 'opacity-30 cursor-not-allowed' : 'hover:text-primary'}`}
                                >
                                    {v}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Selector de origen: Archivo / URL */}
                    <div className="flex border-b border-base-300 shrink-0">
                        {[{ v: 'file', l: 'Archivo' }, { v: 'url', l: 'URL' }].map(({ v, l }) => (
                            <button
                                key={v}
                                type="button"
                                onClick={() => setSource(v)}
                                className={`flex-1 py-1.5 text-xs transition-colors
                                    ${source === v ? 'border-b-2 border-secondary font-semibold text-secondary' : 'text-base-content/50 hover:text-secondary'}`}
                            >
                                {l}
                            </button>
                        ))}
                    </div>

                    {/* Input de URL (solo en modo url) */}
                    {source === 'url' && (
                        <div className="p-2 shrink-0">
                            <input
                                type="url"
                                value={imageUrl}
                                onChange={e => handleUrlChange(e.target.value)}
                                placeholder="https://ejemplo.com/imagen.png"
                                className="input input-bordered input-sm w-full"
                            />
                        </div>
                    )}

                    {/* Área de imagen */}
                    <div className="relative flex-1 flex items-center justify-center overflow-hidden">
                        {source === 'file' && (
                            <input
                                type="file"
                                accept="image/png,image/jpeg,image/webp,image/bmp,image/gif"
                                onChange={handleFileChange}
                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                            />
                        )}

                        {!preview && (
                            <div className="flex flex-col items-center justify-center gap-2 p-4">
                                <IconUpload className="w-10 h-10 text-secondary" />
                                <span className="text-secondary text-sm">
                                    {source === 'url' ? 'Pegá una URL de imagen arriba' : 'Seleccioná una imagen (PNG, BMP, JPG...)'}
                                </span>
                            </div>
                        )}

                        {preview && view === 'original' && (
                            <img src={preview} alt="original" className="max-w-full max-h-full object-contain" />
                        )}

                        {svgResult && view === 'vectorizado' && (
                            <div
                                className="w-full h-full flex items-center justify-center p-2 [&_svg]:max-w-full [&_svg]:max-h-full"
                                dangerouslySetInnerHTML={{ __html: svgResult }}
                            />
                        )}

                        {svgResult && view === 'superpuesto' && (
                            <div className="relative w-full h-full flex items-center justify-center p-2">
                                <img
                                    src={preview}
                                    alt="original"
                                    className="absolute max-w-full max-h-full object-contain"
                                />
                                <div
                                    className="absolute w-full h-full flex items-center justify-center p-2 [&_svg]:max-w-full [&_svg]:max-h-full"
                                    style={{ opacity: 0.7 }}
                                    dangerouslySetInnerHTML={{ __html: svgResult }}
                                />
                            </div>
                        )}

                        {errors.svg && <p className="absolute bottom-2 text-error text-sm">{errors.svg}</p>}
                    </div>
                </div>

                {/* Panel derecho: controles */}
                <div className="flex flex-1 flex-col w-full md:w-3/5 space-y-4 glassCard p-4 h-1/2 md:h-full landscape:h-full landscape:w-3/5 overflow-y-auto">

                    {/* Nombre */}
                    <div className="form-control">
                        <label className="label"><span className="label-text">Nombre del medio*</span></label>
                        <input
                            type="text"
                            value={nombre}
                            onChange={e => setNombre(e.target.value)}
                            className={`input input-bordered w-full ${errors.nombre ? 'input-error' : ''}`}
                            placeholder="Nombre"
                        />
                        {errors.nombre && <span className="text-error text-sm">{errors.nombre}</span>}
                    </div>

                    {/* Permisos */}
                    <div className="form-control">
                        <label className="label"><span className="label-text">Permisos</span></label>
                        <div className="flex gap-4">
                            {[{ v: 'publico', l: 'Público' }, { v: 'privado', l: 'Privado' }].map(({ v, l }) => (
                                <label key={v} className="cursor-pointer flex items-center gap-2">
                                    <input type="radio" name="sharing" value={v}
                                        checked={sharing === v} onChange={() => setSharing(v)}
                                        className="radio radio-primary" />
                                    <span className="label-text">{l}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* Preset */}
                    <button type="button" onClick={applyInkscapePreset}
                        className="btn btn-outline btn-sm w-full gap-2">
                        ⚡ Preset: Inkscape Multicolor
                        <span className="badge badge-ghost badge-xs">Colores · 2 pasadas · Eliminar fondo</span>
                    </button>

                    <div className="divider text-xs">Pre-procesado</div>

                    {/* Eliminar fondo */}
                    <div className="form-control gap-2">
                        <label className="cursor-pointer flex items-center gap-3">
                            <input type="checkbox" checked={removeBg}
                                onChange={e => setRemoveBg(e.target.checked)}
                                className="checkbox checkbox-primary checkbox-sm" />
                            <div>
                                <span className="label-text font-medium">Eliminar fondo</span>
                                <p className="text-xs text-base-content/40">Detecta el color de fondo por las esquinas y lo excluye del trazado</p>
                            </div>
                        </label>
                        {removeBg && engine !== 'inkscape' && (
                            <div className="pl-7">
                                <label className="label py-0">
                                    <span className="label-text text-xs">Tolerancia: <strong>{bgThreshold}</strong></span>
                                </label>
                                <input type="range" min={5} max={100} step={5}
                                    value={bgThreshold} onChange={e => setBgThreshold(Number(e.target.value))}
                                    className="range range-primary range-xs" />
                                <div className="flex justify-between text-xs text-base-content/30"><span>preciso</span><span>agresivo</span></div>
                            </div>
                        )}
                    </div>

                    {/* Upscale cliente — solo aplica a archivos locales */}
                    {source === 'file' && (
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Preescalado (bicubic): <strong>{upscale === 1 ? 'Off' : `${upscale}×`}</strong></span>
                            </label>
                            <div className="flex gap-2">
                                {[1, 2, 3, 4].map(s => (
                                    <button key={s} type="button" onClick={() => setUpscale(s)}
                                        className={`btn btn-sm flex-1 ${upscale === s ? 'btn-primary' : 'btn-ghost border border-base-300'}`}>
                                        {s === 1 ? 'Off' : `${s}×`}
                                    </button>
                                ))}
                            </div>
                            <p className="text-xs text-base-content/40 mt-1">
                                Útil en imágenes de baja resolución. No añade detalle nuevo, suaviza bordes.
                            </p>
                        </div>
                    )}

                    <div className="divider text-xs">Vectorización</div>

                    {/* Motor */}
                    <div className="form-control">
                        <label className="label"><span className="label-text">Motor</span></label>
                        <div className="flex gap-4">
                            {ENGINES.map(({ value, label, hint }) => (
                                <label key={value} className="cursor-pointer flex items-center gap-2">
                                    <input type="radio" name="engine" value={value}
                                        checked={engine === value} onChange={() => setEngine(value)}
                                        className="radio radio-secondary radio-sm" />
                                    <span className="label-text">{label}</span>
                                    {hint && <span className="badge badge-ghost badge-xs">{hint}</span>}
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* N colores */}
                    <div className="form-control">
                        <label className="label">
                            <span className="label-text">Pasadas / Colores: <strong>{nColors === 1 ? '1 (monocolor)' : nColors}</strong></span>
                        </label>
                        <input type="range" min={1} max={16} step={1}
                            value={nColors} onChange={e => setNColors(Number(e.target.value))}
                            className="range range-primary range-sm" />
                        <div className="flex justify-between text-xs text-base-content/40 px-1"><span>1</span><span>16</span></div>
                    </div>

                    {/* Modo de trazado */}
                    <div className="form-control">
                        <label className="label"><span className="label-text">Tipo de trazado</span></label>
                        <div className="flex flex-col gap-1">
                            {MODES.map(({ value, label, hint }) => (
                                <label key={value} className="cursor-pointer flex items-center gap-2">
                                    <input type="radio" name="mode" value={value}
                                        checked={mode === value} onChange={() => setMode(value)}
                                        className="radio radio-primary radio-sm" />
                                    <span className="label-text">{label}</span>
                                    {hint && <span className="badge badge-success badge-xs">{hint}</span>}
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* Sanitizar Bezier — solo curves o inkscape */}
                    {mode !== 'none' && (mode === 'curves' || engine === 'inkscape') && (
                        <div className="form-control">
                            <label className="cursor-pointer flex items-center gap-3">
                                <input
                                    type="checkbox"
                                    checked={sanitize}
                                    onChange={e => setSanitize(e.target.checked)}
                                    className="checkbox checkbox-warning checkbox-sm"
                                />
                                <div>
                                    <span className="label-text">Sanitizar curvas degeneradas</span>
                                    <p className="text-xs text-base-content/40">Convierte Bezier problemáticos en líneas rectas (recomendado para Blender)</p>
                                </div>
                            </label>
                        </div>
                    )}

                    {/* Epsilon — solo mode=points, motor=potrace */}
                    {mode === 'points' && engine !== 'inkscape' && (
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Simplificación: <strong>{epsilon}</strong></span>
                            </label>
                            <input type="range" min={0.001} max={0.02} step={0.001}
                                value={epsilon} onChange={e => setEpsilon(Number(e.target.value))}
                                className="range range-secondary range-sm" />
                            <div className="flex justify-between text-xs text-base-content/40 px-1"><span>más detalle</span><span>más simple</span></div>
                        </div>
                    )}

                    {/* Chips de colores resultado */}
                    {layers.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                            {layers.map(l => (
                                <div key={l.id} className="flex items-center gap-1 text-xs">
                                    <span className="w-4 h-4 rounded-full border border-base-300 inline-block" style={{ background: l.color }} />
                                    {l.color}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Parámetros — espejo del diálogo de Inkscape */}
                    {(mode !== 'points' || engine === 'inkscape') && (
                        <div className="form-control">
                            <button type="button" onClick={() => setShowAdvanced(v => !v)}
                                className="btn btn-ghost btn-xs w-fit gap-1 text-base-content/50">
                                {showAdvanced ? '▾' : '▸'} Detalles {engine === 'inkscape' ? '(Multicolor Inkscape)' : '(Potrace)'}
                            </button>
                            {showAdvanced && (
                                <div className="flex flex-col gap-3 mt-2 pl-2 border-l-2 border-base-300">
                                    {engine === 'inkscape' && (
                                        <div className="flex gap-4">
                                            <label className="cursor-pointer flex items-center gap-2">
                                                <input type="checkbox" checked={smooth}
                                                    onChange={e => setSmooth(e.target.checked)}
                                                    className="checkbox checkbox-sm" />
                                                <span className="label-text text-xs">Suave</span>
                                            </label>
                                            <label className="cursor-pointer flex items-center gap-2">
                                                <input type="checkbox" checked={stack}
                                                    onChange={e => setStack(e.target.checked)}
                                                    className="checkbox checkbox-sm" />
                                                <span className="label-text text-xs">Apilar</span>
                                            </label>
                                        </div>
                                    )}
                                    {[
                                        { show: true,                 label: 'Motas (speckles)', val: turdsize, set: setTurdsize, min: 0, max: 100, step: 1 },
                                        { show: true,                 label: 'Suavizar bordes', val: alphamax, set: setAlphamax, min: 0, max: 1.3333, step: 0.01 },
                                        { show: true,                 label: 'Optimizar', val: opttolerance, set: setOpttolerance, min: 0, max: 5, step: 0.001 },
                                        { show: engine !== 'inkscape', label: 'Brightness cutoff', val: blacklevel, set: setBlacklevel, min: 0, max: 1, step: 0.01 },
                                    ].filter(p => p.show).map(({ label, val, set, min, max, step }) => (
                                        <div key={label}>
                                            <label className="label py-0">
                                                <span className="label-text text-xs">{label}: <strong>{val}</strong></span>
                                            </label>
                                            <input type="range" min={min} max={max} step={step}
                                                value={val} onChange={e => set(Number(e.target.value))}
                                                className="range range-accent range-xs" />
                                        </div>
                                    ))}
                                    <button type="button" onClick={() => { setBlacklevel(0.45); setAlphamax(1.0); setOpttolerance(0.2); setTurdsize(2); setSmooth(false); setStack(false) }}
                                        className="btn btn-xs btn-ghost text-base-content/40">
                                        Restaurar defaults
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {errors.vectorize && <p className="text-error text-sm">{errors.vectorize}</p>}

                    {/* Botones */}
                    <div className="flex flex-col sm:flex-row gap-2 pt-2">
                        <button type="button" onClick={handleVectorize}
                            disabled={loading || (source === 'file' ? !file : !imageUrl.trim())} className="btn btn-secondary w-full sm:w-auto">
                            {loading ? <span className="loading loading-spinner loading-sm" /> : 'Vectorizar'}
                        </button>
                        <button type="submit" disabled={!svgResult}
                            className={`btn btn-primary w-full sm:w-auto ${!svgResult ? 'btn-disabled' : ''}`}>
                            <Icon path={mdiContentSaveOutline} size={1} /> Guardar como medio
                        </button>
                    </div>
                </div>
            </form>
        </div>
    )
}

MediosInputImgEditor.propTypes = {
    onCreated: PropTypes.func,
    onClose: PropTypes.func,
    initialFile: PropTypes.instanceOf(File),
    initialUrl: PropTypes.string,
}

export default MediosInputImgEditor
